import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { DIALOG_HEADING_ID } from "../../utils/constants";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import DOMPurify from "dompurify";

export function DialogCloseButton({ onClick, isDfd, ariaLabel }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`dialog-web-sdk-ico-close ${
        isDfd ? "dfd-close-button modalCloseButton" : ""
      }`}
      data-testid='dialog-x-button'
      aria-label={ariaLabel}
    />
  );
}

export function DialogOverlay({
  fullscreen = false,
  meta = {},
  children,
  paddingRight = "",
  dialogOverlay = true,
  show_icon_warning = false,
  className
}) {
  const dataAttrs = Object.fromEntries(
    Object.entries(meta).map(([key, value]) => {
      return [`data-${key}`, value];
    })
  );
  const style = paddingRight ? { paddingRight } : {};
  return dialogOverlay ? (
    <div
      className={`dialog-overlay-web-sdk ${className} ${
        fullscreen ? "fullscreen" : ""
      } ${show_icon_warning ? "dialog-overlay-with-warning" : ""}`}
      {...dataAttrs}
      style={style}
      data-testid='dialog-overlay'
    >
      {children}
    </div>
  ) : (
    <>{children}</>
  );
}

export function DialogHeader({ children }) {
  const headerRef = useRef(null);

  useEffect(() => {
    headerRef.current.focus();
  }, []);

  return (
    <h1
      ref={headerRef}
      data-testid='dialog-header'
      id={DIALOG_HEADING_ID}
      aria-live='off'
      tabIndex='-1'
    >
      {children}
    </h1>
  );
}

export function DialogSubHeader({ children }) {
  return (
    <h2 data-testid='dialog-subheader' className='dialog-subheader'>
      {children}
    </h2>
  );
}

export function DialogBody({
  fullscreen = false,
  children,
  bodyClass = "",
  type,
  getModalPosition
}) {
  const viewRef = useRef(null);
  const bodyClasses = `dialog-wrapper-web-sdk ${
    fullscreen ? "fullscreen" : ""
  } ${bodyClass && bodyClass.length > 0 ? bodyClass : ""} ${type}`;

  useFocusTrap(viewRef);

  useEffect(() => {
    if (fullscreen) {
      // eslint-disable-next-line no-unused-expressions
      viewRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (getModalPosition) {
      getModalPosition(viewRef.current.getBoundingClientRect());
    }
  }, [fullscreen, viewRef]);

  return (
    <div
      ref={viewRef}
      className={bodyClasses}
      role='dialog'
      aria-modal='true'
      aria-labelledby={DIALOG_HEADING_ID}
    >
      <div
        className={`dialog-web-sdk ${fullscreen ? "fullscreen" : ""}`}
        data-testid='dialog-body'
      >
        {children}
      </div>
    </div>
  );
}

const sanitizedChildHtml = child => {
  const sanitizeHTML = DOMPurify.sanitize(child);
  return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML }} />;
};

export function DialogContent({ children, contentClass = "" }) {
  const childrenExist = children.some(child => typeof child !== "undefined");
  children = children.map(child =>
    typeof child === "string" ? sanitizedChildHtml(child) : child
  );
  const contentClasses = `dialog-content-web-sdk${
    contentClass && contentClass.length > 0 ? ` ${contentClass}` : ""
  }`;
  return childrenExist ? (
    <div className={contentClasses} data-testid='dialog-content'>
      {children}
    </div>
  ) : null;
}

export function DialogHeaderContainer({ children }) {
  return <div className='dialog-header-web-sdk'>{children}</div>;
}

export function DialogFooter({ children }) {
  return <div className='dialog-footer-web-sdk'>{children}</div>;
}

DialogOverlay.propTypes = {
  fullscreen: PropTypes.bool,
  meta: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node.isRequired,
  dialogOverlay: PropTypes.bool,
  paddingRight: PropTypes.string,
  show_icon_warning: PropTypes.bool,
  className: PropTypes.string
};

DialogCloseButton.propTypes = {
  isDfd: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string
};

DialogBody.propTypes = {
  children: PropTypes.node.isRequired,
  fullscreen: PropTypes.bool.isRequired,
  getModalPosition: PropTypes.func,
  bodyClass: PropTypes.string,
  type: PropTypes.string
};

DialogContent.propTypes = {
  contentClass: PropTypes.string,
  children: PropTypes.node.isRequired
};

DialogHeaderContainer.propTypes = {
  children: PropTypes.node
};

DialogHeaderContainer.defaultProps = {
  children: null
};

DialogFooter.propTypes = {
  children: PropTypes.node.isRequired
};

DialogHeader.propTypes = {
  children: PropTypes.node.isRequired
};

DialogSubHeader.propTypes = {
  children: PropTypes.node.isRequired
};
